import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { labels } from "../i18n/translations";
import { fetchPerfumes } from "../lib/api";
import ProductCard from "../components/ProductCard";
import SelectionCard from "../components/SelectionCard";
import WhatsAppButton from "../components/WhatsAppButton";
import { ArrowRight } from "lucide-react";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_fragranze-elite/artifacts/4idzimu4_foto%20homme%20emergent.jpg";

export const Home = () => {
  const [highlights, setHighlights] = useState([]);
  const [selections, setSelections] = useState([]);
  const [stats, setStats] = useState({ count: 0, brands: 0 });

  useEffect(() => {
    let mounted = true;
    fetchPerfumes()
      .then((data) => {
        if (!mounted) return;
        const visible = data.filter((p) => p.visible);
        const ranked = [...visible].sort(
          (a, b) => (b.main_image ? 1 : 0) - (a.main_image ? 1 : 0),
        );
        setHighlights(ranked.slice(0, 4));
        setSelections(
          visible
            .filter((p) => p.featured_in_selections)
            .sort((a, b) => a.slot_number - b.slot_number)
            .slice(0, 10),
        );
        const brands = new Set(visible.map((p) => p.brand).filter(Boolean));
        setStats({ count: visible.length, brands: brands.size });
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <main data-testid="page-home">
      <section
        data-testid="hero-section"
        className="relative min-h-[88vh] md:min-h-[92vh] flex items-end overflow-hidden"
      >
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-white/0" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/40 via-transparent to-transparent" />
        </div>

        <div className="relative w-full px-6 md:px-12 lg:px-20 pb-16 md:pb-24 lg:pb-32">
          <div className="max-w-3xl">
            <div
              className="uppercase tracking-[0.32em] text-[11px] text-[#9A7B3E] mb-6 animate-[fadeUp_0.8s_ease-out_both]"
              data-testid="hero-overline"
            >
              Collezione Privata · Niche & Luxury
            </div>
            <h1
              data-testid="hero-title"
              className="font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-black animate-[fadeUp_0.9s_ease-out_0.1s_both]"
            >
              {labels.home.title}
            </h1>
            <p
              data-testid="hero-subtitle"
              className="mt-6 md:mt-8 text-base md:text-lg text-black/70 font-light max-w-xl leading-relaxed whitespace-pre-line animate-[fadeUp_1s_ease-out_0.2s_both]"
            >
              {labels.home.subtitle}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 animate-[fadeUp_1.1s_ease-out_0.3s_both]">
              <Link
                to="/catalogo"
                data-testid="hero-cta-catalog"
                className="inline-flex items-center justify-center gap-3 bg-black text-white hover:bg-[#C5A059] transition-all duration-300 uppercase tracking-[0.22em] text-xs py-4 px-8 group"
              >
                {labels.home.cta}
                <ArrowRight
                  strokeWidth={1.25}
                  className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                />
              </Link>
              <Link
                to="/contatti"
                data-testid="hero-cta-contact"
                className="inline-flex items-center justify-center gap-3 bg-transparent text-black border border-black hover:bg-black hover:text-white transition-all duration-300 uppercase tracking-[0.22em] text-xs py-4 px-8"
              >
                {labels.nav.contact}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="philosophy-section" className="px-6 md:px-12 lg:px-20 py-24 md:py-32 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-6">
              {labels.home.philosophy_overline}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-black">
              {labels.home.philosophy_title}
            </h2>
            <div className="mt-8 max-w-xl space-y-5 text-base md:text-lg text-black/65 font-light leading-relaxed">
              {labels.home.philosophy_text.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>

            <div className="mt-12 grid grid-cols-3 gap-4 md:gap-8 max-w-xl">
              <div>
                <div className="font-heading text-3xl md:text-4xl text-[#C5A059]">{stats.count}</div>
                <div className="mt-2 uppercase tracking-[0.22em] text-[10px] text-black/50">{labels.home.stats.curated}</div>
              </div>
              <div>
                <div className="font-heading text-3xl md:text-4xl text-[#C5A059]">{stats.brands}</div>
                <div className="mt-2 uppercase tracking-[0.22em] text-[10px] text-black/50">{labels.home.stats.brands}</div>
              </div>
              <div>
                <div className="font-heading text-3xl md:text-4xl text-[#C5A059]">1:1</div>
                <div className="mt-2 uppercase tracking-[0.22em] text-[10px] text-black/50">{labels.home.stats.private}</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative aspect-[4/5] overflow-hidden bg-[#F4F4F4]">
              <img
                src="https://customer-assets.emergentagent.com/job_fragranze-elite/artifacts/v3ez4101_uomo%20donna%20emergent.jpg"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="why-section"
        className="border-y border-black/10 bg-[#FBF9F4] px-6 md:px-12 lg:px-20 py-24 md:py-32"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-6">
            {labels.home.why_overline}
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-black">
            {labels.home.why_title}
          </h2>
          <div
            className="mx-auto mt-10 w-12 h-px bg-[#C5A059]"
            aria-hidden="true"
          />
          <div className="mt-10 space-y-6 text-base md:text-lg text-black/65 font-light leading-relaxed">
            {labels.home.why_text.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </div>
      </section>

      {selections.length > 0 && (
        <section
          data-testid="selections-section"
          className="px-6 md:px-12 lg:px-20 py-24 md:py-32"
        >
          <div className="max-w-3xl mb-14 md:mb-20">
            <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-5">
              {labels.home.selections_overline}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
              {labels.home.selections_title}
            </h2>
            <p className="mt-6 text-base md:text-lg text-black/65 font-light leading-relaxed">
              {labels.home.selections_subtitle}
            </p>
          </div>

          <div
            data-testid="selections-grid"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-8 md:gap-10"
          >
            {selections.map((p) => (
              <SelectionCard key={p.id} perfume={p} />
            ))}
          </div>
        </section>
      )}

      <section data-testid="highlights-section" className="px-6 md:px-12 lg:px-20 py-24 md:py-32">        <div className="flex items-end justify-between flex-wrap gap-6 mb-14 md:mb-20">
          <div>
            <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-4">
              {labels.home.highlights_overline}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              {labels.home.highlights_title}
            </h2>
          </div>
          <Link
            to="/catalogo"
            data-testid="highlights-view-all"
            className="uppercase tracking-[0.22em] text-[11px] border-b border-black pb-1 hover:text-[#C5A059] hover:border-[#C5A059] transition-colors"
          >
            {labels.home.view_all}
          </Link>
        </div>

        {highlights.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
            {highlights.map((p) => (
              <ProductCard key={p.id} perfume={p} />
            ))}
          </div>
        )}
      </section>

      <section
        data-testid="request-section"
        className="border-t border-black/10 bg-[#FBF9F4] px-6 md:px-12 lg:px-20 py-24 md:py-32"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="uppercase tracking-[0.32em] text-[11px] text-[#C5A059] mb-6">
            {labels.home.request_overline}
          </div>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-black">
            {labels.home.request_title}
          </h2>
          <div className="mx-auto mt-10 w-12 h-px bg-[#C5A059]" aria-hidden="true" />
          <div className="mt-10 space-y-6 text-base md:text-lg text-black/65 font-light leading-relaxed">
            {labels.home.request_text.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <WhatsAppButton
              testId="request-whatsapp-cta"
              message={labels.home.request_msg}
              size="lg"
              variant="gold"
            >
              {labels.home.request_cta}
            </WhatsAppButton>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
